import React, { useMemo } from 'react';
import { buildTree, nearestNeighbor, Coordinate } from './kd-tree';
import { AbstractHoverZoneBidirectionalProps } from './types';
import { throttle } from 'lodash';
import { AbstractHoverZoneBase } from './AbstractHoverZoneBase';

export const AbstractHoverZoneBidirectional = <D,>({
  children,
  dataPoints,
  xScale,
  dataToX,
  tooltipX,
  setTooltipX,
  yScale,
  dataToY,
  tooltipY,
  setTooltipY,
  setTooltipData,
  getTooltipData,
  ...rest
}: AbstractHoverZoneBidirectionalProps<D>) => {
  const tree = useMemo(() => {
    const points = dataPoints.map((data, index) => ({
      data,
      coordinates: [xScale(dataToX(data)), yScale(dataToY(data))] as Coordinate,
      index,
    }));
    return buildTree(points);
  }, [dataPoints, dataToX, dataToY, xScale, yScale]);

  const throttleMouseMove = useMemo(
    () =>
      throttle((e, getTooltipData, setTooltipData, setTooltipX, setTooltipY, elementSize) => {
        if (!elementSize || !tree) return;
        const { left, top } = elementSize;
        const x = e.pageX - left;
        const y = e.pageY - top;
        const clientPoint = [x, y] as Coordinate;

        const closestPoint = nearestNeighbor(tree, clientPoint);

        setTooltipX(closestPoint.point[0]);
        setTooltipY(closestPoint.point[1]);
        setTooltipData(getTooltipData(closestPoint.data));
      }, 16),
    [tree],
  );

  return tree ? (
    <AbstractHoverZoneBase<D>
      {...rest}
      positionKey={`${tooltipX}${tooltipY}`}
      onMouseLeave={() => {
        throttleMouseMove.cancel();
        setTooltipData(null);
        setTooltipX(undefined);
        setTooltipY(undefined);
      }}
      onMouseMove={(e, elementOffset) =>
        throttleMouseMove(
          e,
          getTooltipData,
          setTooltipData,
          setTooltipX,
          setTooltipY,
          elementOffset,
        )
      }
      tooltipX={tooltipX}
      tooltipY={tooltipY}
    >
      {children}
    </AbstractHoverZoneBase>
  ) : null;
};
